import React from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ResourceLinkKind } from '../../types';
import { t } from '../../utils/translations';
import ResourceKindIcon, { resourceKindShortLabel } from './ResourceKindIcon';
import {
  RESOURCE_KIND_ORDER,
  ResourceKindFilter,
} from './resourcesUtils';

interface ResourcesSearchToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  kindFilter: ResourceKindFilter;
  onKindFilterChange: (value: ResourceKindFilter) => void;
  /** Counts per kind for the current query, ignoring the kind filter. */
  kindCounts: Record<ResourceLinkKind, number>;
  totalCount: number;
  filteredCount: number;
  showResultsMeta: boolean;
}

interface KindPillProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
}

const KindPill: React.FC<KindPillProps> = ({ label, count, active, onClick, icon }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition duration-200 active:scale-[0.98] ${
      active
        ? 'bg-brand-50 text-brand-700'
        : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
    }`}
  >
    {icon}
    {label}
    <span className={active ? 'text-brand-600' : 'text-slate-400'}>{count}</span>
  </button>
);

const ResourcesSearchToolbar: React.FC<ResourcesSearchToolbarProps> = ({
  query,
  onQueryChange,
  kindFilter,
  onKindFilterChange,
  kindCounts,
  totalCount,
  filteredCount,
  showResultsMeta,
}) => {
  const availableKinds = RESOURCE_KIND_ORDER.filter((kind) => kindCounts[kind] > 0);
  const countLabel =
    filteredCount === 1 ? t.resources.searchResultSingular : t.resources.searchResultPlural;

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          id="resources-search"
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t.resources.searchPlaceholder}
          aria-label={t.resources.searchLabel}
          className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 [&::-webkit-search-cancel-button]:appearance-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            title={t.resources.clearSearch}
            aria-label={t.resources.clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {showResultsMeta && (
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {availableKinds.length > 1 ? (
            <div className="flex flex-wrap gap-2">
              <KindPill
                label={t.resources.filterAllKinds}
                count={totalCount}
                active={kindFilter === 'all'}
                onClick={() => onKindFilterChange('all')}
              />
              {availableKinds.map((kind) => (
                <KindPill
                  key={kind}
                  label={resourceKindShortLabel(kind)}
                  count={kindCounts[kind]}
                  active={kindFilter === kind}
                  onClick={() => onKindFilterChange(kind)}
                  icon={<ResourceKindIcon kind={kind} className="h-3.5 w-3.5 shrink-0" />}
                />
              ))}
            </div>
          ) : (
            <span />
          )}
          <span className="shrink-0 text-xs font-medium text-slate-500">
            {filteredCount} {countLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default ResourcesSearchToolbar;
